import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import styles from './SearchBar.module.css';
import baseStyles from '../../styles.module.css';
import SearchBarController from './SearchBarController';

const useSearchBarListeners = (controller: SearchBarController) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLAhaSearchFieldElement>(null);

  useEffect(() => {
    const container = containerRef.current!;
    const input = inputRef.current!;

    const select = () => {
      input.focus();
      // @ts-expect-error private property of the input element, but there's no other way to access it
      input.inputField.select();
    };

    setTimeout(select, 100);

    const getHandler = (e: KeyboardEvent): (() => void) | undefined => {
      e.stopPropagation();
      if (e.metaKey || e.ctrlKey) {
        switch (e.key) {
          case 'g':
            return () => controller.actionNext();
          case 'f':
            return select;
        }
      }

      switch (e.key) {
        case 'Enter':
        case 'ArrowDown':
          return () => controller.actionNext();
        case 'ArrowUp':
          return () => controller.actionPrevious();
        case 'Escape':
          return () => controller.actionHide();
      }
    };

    const handler = (e: KeyboardEvent) => {
      const func = getHandler(e);

      // just in case the surrounding element has gained focus somehow
      input.focus();

      if (func) {
        e.preventDefault();
        e.stopPropagation();
        func();
      }
    };
    container.addEventListener('keydown', handler);

    const clickOutsideHandler = (e: KeyboardEvent) => {
      if (!container.contains(e.target as Node)) {
        controller.actionHide();
      }
    };
    // use mousedown instead of click to avoid picking up the click that
    // opened the search bar, and immediately closing it
    document.body.addEventListener('mousedown', clickOutsideHandler);

    // refresh results when reopened
    controller.actionUpdateResults();

    return () => {
      container.removeEventListener('keydown', handler);
      document.body.removeEventListener('mousedown', clickOutsideHandler);
      controller.clearHighlights();
    };
  }, [controller]);

  return { containerRef, inputRef };
};

const SearchBarContent = ApplicationView(() => {
  const controller = useController(SearchBarController);
  const { containerRef, inputRef } = useSearchBarListeners(controller);

  const { results, searchTerm } = controller.state;
  const currentIndex = controller.getCurrentIndex();

  return (
    <div
      ref={containerRef}
      tabIndex={-1} // ensure element can capture keydown events
      className={classNames(styles.searchBar)}
      data-testid='search-bar'
    >
      <div className={classNames(styles.searchBarInput)}>
        <aha-search-field
          throttled={false}
          ref={inputRef}
          autofocus
          value={searchTerm ?? ''}
          placeholder='Search'
          onInput={e => {
            controller.actionUpdateTerm(e.target.value);
          }}
          style={{
            width: '100%',
            boxShadow: 'none',
            borderRadius: '0',
            fontSize: '14px',
          }}
        />
      </div>

      {results && searchTerm && (
        <div className={styles.searchBarResultsWrap}>
          {results.length ? (
            <>
              <div className={styles.searchBarResultsHeader}>
                <p>
                  {results.length} result{results.length !== 1 ? 's' : ''}
                </p>
                <a
                  className={styles.searchBarNavLink}
                  onClick={() => controller.actionPrevious()}
                >
                  <aha-icon icon='fa-solid fa-angle-up' />
                </a>
                <a
                  className={styles.searchBarNavLink}
                  onClick={() => controller.actionNext()}
                >
                  <aha-icon icon='fa-solid fa-angle-down' />
                </a>
              </div>
              <div
                className={classNames(
                  styles.searchBarResults,
                  baseStyles.minimalScrollbar
                )}
                ref={el => {
                  controller.resultsEl = el!;
                }}
              >
                {results.map((result, i) => (
                  <a
                    key={result.object.id}
                    className={classNames(styles.searchBarResult, {
                      [styles.current]: currentIndex === i,
                    })}
                    onClick={() => {
                      controller.actionSetCurrent(i);
                    }}
                  >
                    {result.preview.before}
                    <strong>{result.preview.match}</strong>
                    {result.preview.after}
                  </a>
                ))}
              </div>
            </>
          ) : (
            <div className={styles.searchBarNoResults}>No results</div>
          )}
        </div>
      )}
    </div>
  );
});

interface SearchBarProps {
  visible: boolean;
}

// wrapper component because we want the state to persist when it's hidden
const SearchBar: React.FC<SearchBarProps> = ({ visible }) => {
  if (!visible) {
    return null;
  }

  return <SearchBarContent />;
};

export default StartControllerScope(SearchBarController, SearchBar);
